import React, { useEffect, useState } from "react";
import { Modal, Button, Card, Row, Col, Form, ProgressBar } from "react-bootstrap";
import { FaRegSquarePlus } from "react-icons/fa6";
import Loader from "../loader/Loader";
import config from "../../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { getAuthToken } from "../../utils/token";
import { formatDate } from "../../utils/formatDate";
// ---- Style tokens (kept local so this drops into any react-bootstrap layout) ----
const COLORS = {
    navy: "#0f1b2d",
    blue: "#2f6fed",
    blueSoft: "#cfe0ff",
    green: "#22a06b",
    greenHover: "#1c8a5b",
    red: "#e5484d",
    redHover: "#cf3d42",
    border: "#e3e7ee",
    muted: "#8a93a3",
    ink: "#12203a",
};
// ---- API endpoints ----
// Adjust these paths to match your actual backend routes.
const ENDPOINTS = {
    dashboard: `${config.API}/production/dashboard`,
    startCycle: `${config.API}/startcycle`,
    endCycle: `${config.API}/endcycle`,
};

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getAuthToken()}` },
});

export default function ProductionDashboard() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [dashboard, setDashboard] = useState(null); // { target, plates, shift, operator, machineId }
    const [activePlate, setActivePlate] = useState(null);
    const [quantity, setQuantity] = useState("0");
    const [cycleActive, setCycleActive] = useState(false);
    const [showEndConfirm, setShowEndConfirm] = useState(false);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(ENDPOINTS.dashboard, authHeaders());
            setDashboard(data);
            if (data?.plates?.length) {
                setActivePlate((prev) => prev || data.plates[0].key);
            }
            // setCycleActive(!!data?.cycleActive);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    const press = (val) => {
        if (val === "clear") {
            setQuantity("0");
        } else if (val === "back") {
            setQuantity((q) => (q.length > 1 ? q.slice(0, -1) : "0"));
        } else {
            setQuantity((q) => (q === "0" ? String(val) : q + String(val)));
        }
    };

    const handleStartCycle = async () => {
        if (!activePlate || Number(quantity) <= 0) {
            toast.warning("Select a plate and enter a quantity greater than 0");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(
                ENDPOINTS.startCycle,
                { plateType: activePlate, quantity: Number(quantity) },
                authHeaders()
            );
            setCycleActive(true);
            fetchDashboard();
            toast.success("Cycle started");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to start cycle");
        } finally {
            setSubmitting(false);
        }
    };

    const confirmEndCycle = async () => {
        try {
            setSubmitting(true);
            await axios.post(
                ENDPOINTS.endCycle,
                {
                    plateType: activePlate,
                    quantity: Number(quantity), // or qty: quantity
                },
                authHeaders()
            );
            toast.success("Cycle ended");
            setCycleActive(false);
            setShowEndConfirm(false);
            setQuantity(""); // Optional: clear after ending
            fetchDashboard();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to end cycle");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    const plates = dashboard?.plates || [];
    const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9, "clear", 0, "back"];

    return (
        <div style={{ backgroundColor: "#eef1f6", minHeight: "100vh" }}>
            {/* Topbar */}
            <div
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{ backgroundColor: COLORS.navy }}
            >
                <div>
                    <div className="text-white fw-bold fs-5">PRODUCTION DASHBOARD</div>
                    <div style={{ color: "#8ea2c0", fontSize: "0.75rem" }}>
                        Machine {dashboard?.machineId || "--"}
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <span
                        className="fw-semibold rounded px-3 py-2"
                        style={{ backgroundColor: "rgba(255,255,255,0.08)", color: COLORS.blueSoft, fontSize: "0.8rem" }}
                    >
                        {dashboard?.shift || "Shift"}
                    </span>
                    <span
                        className="fw-medium rounded px-3 py-2"
                        style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#e7ecf5", fontSize: "0.8rem" }}
                    >
                        {formatDate(new Date())}
                    </span>
                </div>
            </div>

            <div className="p-4">
                <Row className="g-4">
                    {/* Sidebar */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <Card.Body>
                                <div
                                    className="d-flex justify-content-between align-items-center rounded-3 px-3 py-3 mb-3"
                                    style={{ backgroundColor: COLORS.navy }}
                                >
                                    <span className="text-white fw-bold">{dashboard?.current ?? "--"} Production</span>
                                    <span
                                        className="text-white fw-bold rounded-pill px-3 py-1"
                                        style={{ backgroundColor: COLORS.blue, fontSize: "0.72rem" }}
                                    >
                                        Target: {dashboard?.target ?? "--"} Sets
                                    </span>
                                </div>

                                {plates.map((plate) => {
                                    const pct = plate.target
                                        ? Math.round((plate.current / plate.target) * 100)
                                        : 0;
                                    return (
                                        <div
                                            key={plate.key}
                                            className="rounded-3 p-3 mb-3"
                                            style={{ border: `1px solid ${COLORS.border}` }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <span
                                                        className="d-inline-block rounded-circle me-2"
                                                        style={{ width: 8, height: 8, backgroundColor: COLORS.blue }}
                                                    />
                                                    <span className="fw-bold" style={{ color: COLORS.ink, fontSize: "0.9rem" }}>
                                                        {plate.label}
                                                    </span>
                                                    <span className="ms-2" style={{ color: COLORS.muted, fontSize: "0.72rem" }}>
                                                        {plate.ratio}
                                                    </span>
                                                </div>
                                                <span className="fw-bold" style={{ color: COLORS.ink, fontSize: "0.9rem" }}>
                                                    {plate.current} / {plate.target}
                                                </span>
                                            </div>
                                            <ProgressBar
                                                now={pct}
                                                className="mt-2"
                                                style={{ height: 8, backgroundColor: "#eef1f6" }}
                                                variant="primary"
                                            />
                                            <div className="text-end mt-1">
                                                <span className="fw-bold" style={{ color: COLORS.blue, fontSize: "0.78rem" }}>
                                                    {pct}%
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="mt-4">
                                    <div className="fw-bold mb-2" style={{ color: COLORS.ink }}>
                                        Operational details
                                    </div>
                                    <dl className="mb-0">
                                        <dt className="fw-semibold" style={{ color: COLORS.muted, fontSize: "0.78rem" }}>
                                            Shift
                                        </dt>
                                        <dd className="fw-bold mb-2" style={{ color: COLORS.ink }}>
                                            {dashboard?.shift || "--"}
                                        </dd>
                                        <dt className="fw-semibold" style={{ color: COLORS.muted, fontSize: "0.78rem" }}>
                                            Operator
                                        </dt>
                                        <dd className="fw-bold mb-2" style={{ color: COLORS.ink }}>
                                            {dashboard?.operator || "--"}
                                        </dd>
                                        <dt className="fw-semibold" style={{ color: COLORS.muted, fontSize: "0.78rem" }}>
                                            Machine ID
                                        </dt>
                                        <dd className="fw-bold mb-0" style={{ color: COLORS.ink }}>
                                            {dashboard?.machineId || "--"}
                                        </dd>
                                    </dl>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Input controls */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center gap-2 fw-bold fs-5 mb-4" style={{ color: COLORS.ink }}>
                                    <span
                                        className="rounded-2 d-flex align-items-center justify-content-center text-white"
                                        style={{ width: 30, height: 30, backgroundColor: COLORS.blue }}
                                    >
                                        <FaRegSquarePlus size={16} />
                                    </span>
                                    INPUT CONTROLS
                                </div>

                                <Row className="g-3 mb-4">
                                    {plates.map((plate) => {
                                        const active = activePlate === plate.key;
                                        return (
                                            <Col xs={4} key={plate.key}>
                                                <Button
                                                    onClick={() => setActivePlate(plate.key)}
                                                    className="w-100 py-3 fw-bold d-flex align-items-center gap-2"
                                                    style={{
                                                        backgroundColor: active ? COLORS.blue : "#fff",
                                                        borderColor: active ? COLORS.blue : COLORS.border,
                                                        color: active ? "#fff" : "#5b667a",
                                                    }}
                                                >
                                                    <span
                                                        className="d-inline-block rounded-circle"
                                                        style={{
                                                            width: 9,
                                                            height: 9,
                                                            backgroundColor: active ? "#fff" : "#b7c0cf",
                                                        }}
                                                    />
                                                    {plate.label}
                                                </Button>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                <Form.Label
                                    className="fw-bold"
                                    style={{ color: COLORS.muted, fontSize: "0.78rem", letterSpacing: "0.03em" }}
                                >
                                    QUANTITY TO PROCESS
                                </Form.Label>
                                <div
                                    className="rounded-3 px-4 py-3 d-flex align-items-baseline gap-2 mb-4"
                                    style={{ backgroundColor: "#f4f6fa", border: `1px solid ${COLORS.border}` }}
                                >
                                    <span className="fw-bolder" style={{ color: COLORS.ink, fontSize: "2.4rem", lineHeight: 1 }}>
                                        {quantity}
                                    </span>
                                    <span className="fw-semibold" style={{ color: COLORS.muted }}>
                                        Units
                                    </span>
                                </div>

                                <Row className="g-3 mb-4">
                                    {keys.map((k, i) => (
                                        <Col xs={4} key={i}>
                                            <Button
                                                variant="light"
                                                className="w-100 py-3 fw-bold"
                                                style={{ border: `1px solid ${COLORS.border}`, color: COLORS.ink }}
                                                onClick={() => press(k)}
                                            >
                                                {k === "clear" ? "Clear" : k === "back" ? "⌫" : k}
                                            </Button>
                                        </Col>
                                    ))}
                                </Row>

                                <Row className="g-3">
                                    <Col xs={6}>
                                        <Button
                                            className="w-100 py-3 fw-bold border-0"
                                            style={{ backgroundColor: COLORS.green }}
                                            disabled={submitting || cycleActive}
                                            onClick={handleStartCycle}
                                        >
                                            {submitting ? "Starting..." : "▶ START CYCLE"}
                                        </Button>
                                    </Col>
                                    <Col xs={6}>
                                        <Button
                                            className="w-100 py-3 fw-bold border-0"
                                            style={{ backgroundColor: COLORS.red }}
                                            disabled={submitting || !cycleActive}
                                            onClick={() => setShowEndConfirm(true)}
                                        >
                                            ■ END CYCLE
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Confirm end cycle */}
            <Modal show={showEndConfirm} onHide={() => setShowEndConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>End Cycle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to end the current production cycle
                    {activePlate ? ` for ${plates.find((p) => p.key === activePlate)?.label}` : ""}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowEndConfirm(false)}>
                        Cancel
                    </Button>
                    <Button
                        style={{ backgroundColor: COLORS.red, border: "none" }}
                        disabled={submitting}
                        onClick={confirmEndCycle}
                    >
                        {submitting ? "Ending..." : "End Cycle"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}